import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { T685Repository } from '@asm/features/sync/repositories/t685.repository';
import { T683SRepository } from '@asm/features/sync/repositories/t683s.repository';
import { LocalTaxCondition, ManualCondition, PricingResult, PricingStep } from '@asm/features/sync/services/pricing/pricing.interfaces';
import { DefaultLocalPricingCalculationService } from '@asm/features/sync/services/pricing/default-local-pricing-calculation.service';
import { DefaultLocalPricingConditionsService } from '@asm/features/sync/services/pricing/default-local-pricing-conditions.service';
import { DefaultLocalPricingContextService } from '@asm/features/sync/services/pricing/default-local-pricing-context.service';
import { DefaultLocalTaxService } from '@asm/features/sync/services/pricing/default-local-tax.service';

/** Serialization format mirrors online `DefaultPricingCalculationService.serializeConditionsForStorage`. */
export function serializePricingSteps(steps: PricingStep[], currency: string): string[] {
  return steps.map(step =>
    [
      step.stepNumber,
      step.condition,
      step.description ?? '',
      step.baseValue,
      step.conditionRate,
      step.conditionUnit,
      step.calculationType ?? '',
      step.calculatedAmount,
      step.resultValue,
      step.conditionClass ?? '',
      currency,
      step.accessSequence ?? '',
      step.conditionTable ?? '',
      step.isApplied ? '1' : '0',
      step.isManual ? '1' : '0',
      step.baseConditionKey ?? '',
      step.isSubtotal ? '1' : '0',
    ].join('|'),
  );
}

@Injectable({ providedIn: 'root' })
export class DefaultLocalPricingService {
  private readonly t685Repo = inject(T685Repository);
  private readonly t683sRepo = inject(T683SRepository);
  private readonly calc = inject(DefaultLocalPricingCalculationService);
  private readonly cond = inject(DefaultLocalPricingConditionsService);
  private readonly ctx = inject(DefaultLocalPricingContextService);
  private readonly tax = inject(DefaultLocalTaxService);

  async getPrice(
    productCode: string,
    qty: number,
    b2bUnitId: string,
    warehouseCode?: string,
    manualConditions: ManualCondition[] = [],
  ): Promise<PricingResult> {
    const context = await this.ctx.prepareSearchContext(productCode, b2bUnitId, warehouseCode);

    // Mirror online DefaultPricingService.getConditions():
    // 1. Load T683S (pricing procedure steps) to get the list of active condition codes.
    // 2. Filter T685 to only the condition types referenced by the procedure.
    //    If T683S is empty (not synced yet), fall back to all T685 records.
    const t683sAll = await firstValueFrom(this.t683sRepo.getAll());
    const schemaKschls = new Set(t683sAll.map(s => s.KSCHL).filter((k): k is string => Boolean(k)));

    const allT685 = await firstValueFrom(this.t685Repo.getAll());
    const allConditions = schemaKschls.size > 0 ? allT685.filter(c => schemaKschls.has(c.KSCHL)) : allT685;

    const conditionRecords = await this.cond.getPricingConditions(context, allConditions);
    const pricingResult = await this.calc.calculate(
      conditionRecords,
      context,
      manualConditions,
      allConditions,
      qty,
      'BRL',
    );
    context['price'] = pricingResult.finalPrice;

    const taxResult = await this.tax.calculate(context);
    if (taxResult?.conditions?.length) {
      this.appendTaxSteps(pricingResult, taxResult.conditions, qty);
    }

    return pricingResult;
  }

  private appendTaxSteps(pricingResult: PricingResult, taxConditions: LocalTaxCondition[], qty: number): void {
    const safeQty = qty > 0 ? qty : 1;
    let taxPerUnit = 0;
    let icmsPerUnit = 0;
    let ipiPerUnit = 0;
    let stPerUnit = 0;

    for (const taxCondition of taxConditions) {
      const valuePerUnit = this.roundFinal(Number(taxCondition.value ?? 0));
      const step: PricingStep = {
        stepNumber: pricingResult.steps.length + 1,
        condition: taxCondition.condition,
        description: taxCondition.description ?? taxCondition.condition,
        baseValue: this.roundFinal(Number(taxCondition.baseValue ?? 0)),
        conditionRate: this.roundFinal(Number(taxCondition.rate ?? 0)),
        conditionUnit: '%',
        calculatedAmount: valuePerUnit,
        resultValue: this.roundFinal(pricingResult.finalPrice + taxPerUnit + valuePerUnit),
        conditionClass: 'C',
        calculationType: 'A',
        isSubtotal: false,
        isManual: false,
        isApplied: valuePerUnit > 0,
        baseConditionKey: null,
        accessSequence: null,
        conditionTable: 'TAX',
        conditionRecord: taxCondition,
      };

      pricingResult.steps.push(step);
      taxPerUnit += valuePerUnit;

      if (taxCondition.condition === 'BX13') {
        icmsPerUnit += valuePerUnit;
      }

      if (taxCondition.condition === 'BX23') {
        ipiPerUnit += valuePerUnit;
      }

      if (taxCondition.condition === 'BX41') {
        stPerUnit += valuePerUnit;
      }
    }

    pricingResult.icms = this.roundFinal(icmsPerUnit);
    pricingResult.ipi = this.roundFinal(ipiPerUnit);
    pricingResult.st = this.roundFinal(stPerUnit);
    pricingResult.tax = this.roundFinal(taxPerUnit);
    pricingResult.totalTax = this.roundFinal(pricingResult.tax * safeQty);
    pricingResult.finalPrice = this.roundFinal(pricingResult.finalPrice + pricingResult.tax);
    pricingResult.finalTotal = this.roundFinal(pricingResult.finalPrice * safeQty);
    pricingResult.totalValue = this.roundFinal(pricingResult.finalTotal - pricingResult.totalTax);
    pricingResult.formatedICMS = this.formatCurrency(pricingResult.icms, pricingResult.currency);
    pricingResult.formatedIPI = this.formatCurrency(pricingResult.ipi, pricingResult.currency);
    pricingResult.formatedST = this.formatCurrency(pricingResult.st, pricingResult.currency);
    pricingResult.formatedFinalPrice = this.formatCurrency(pricingResult.finalPrice, pricingResult.currency);
  }

  private formatCurrency(value: number, currency: string): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency || 'BRL',
    }).format(value);
  }

  /**
   * Serializes pricing steps into the pipe-delimited format used online.
   * Allows the frontend to reconstruct the full price formation breakdown.
   */
  serializeConditionsForStorage(result: PricingResult): string[] {
    return serializePricingSteps(result.steps, result.currency);
  }

  private roundFinal(value: number): number {
    return Number.parseFloat(Number(value || 0).toFixed(2));
  }
}
